/** Habi journey: a useful next step derived from current verified facts, not slide position.
 * Facts are produced by trusted application adapters. Never deserialize them from model output
 * or onboarding storage. Customer content/IDs are intentionally absent from model-facing steps.
 */
export type Knowledge = 'yes' | 'no' | 'unknown';
export type Persona = 'owner' | 'invited';
export interface HabiFacts {
  observedAt: number;
  persona: Persona | null;
  numberConnected: Knowledge;
  numberSelected?: Knowledge;
  colleagueSelected?: Knowledge;
  receiving?: boolean;
  selfCanAccess: Knowledge;
  colleagueActive: Knowledge;
  invitationPending: Knowledge;
  colleagueCanAccess: Knowledge;
  conversationSelected: Knowledge;
  assignedToColleague: Knowledge;
  /** Must come from a second authenticated user, for the same selected conversation and number. */
  recipientVerified: Knowledge;
  canConnect: boolean;
  canInvite: boolean;
  canManageAccess: boolean;
  canAssign: boolean;
  uncertainWrite: boolean;
}
export type HabiStepId = 'persona' | 'refresh' | 'reconcile' | 'connect' | 'owner-help' |
  'self-access' | 'invite' | 'wait-invite' | 'colleague-access' | 'select-conversation' |
  'choose-number' | 'choose-colleague' | 'wait-assignment' | 'assign' | 'recipient-check' | 'complete';
export interface HabiStep {
  id: HabiStepId;
  message: string;
  action: null | 'choose-persona' | 'refresh-evidence' | 'open-number-setup' | 'prepare-owner-request' |
    'choose-colleague' | 'open-access' | 'open-invite' | 'open-conversations' | 'propose-assignment' | 'verify-recipient';
  /** open = navigation only; propose = separate reviewed mutation, never an immediate write. */
  kind: 'question' | 'read' | 'open' | 'propose' | 'wait' | 'done';
}
const step = (id: HabiStepId, message: string, action: HabiStep['action'], kind: HabiStep['kind']): HabiStep => ({id,message,action,kind});
const refresh = () => step('refresh','Controllo a che punto siamo, così non ti faccio ripetere passaggi già fatti.','refresh-evidence','read');
const help = (message: string) => step('owner-help',message,'prepare-owner-request','propose');

export function nextHabiStep(f: HabiFacts, now = Date.now()): HabiStep {
  if (!f.persona) return step('persona','Stai preparando lo spazio per il team oppure sei stato invitato da un collega?','choose-persona','question');
  if (f.uncertainWrite) return step('reconcile','L’ultima modifica non è ancora verificata. Controllo il risultato prima di ripeterla.','refresh-evidence','read');
  if (!Number.isFinite(f.observedAt) || f.observedAt > now || now - f.observedAt > 60_000) return refresh();
  if (f.numberConnected === 'unknown') return refresh();
  if (f.numberConnected === 'no') {
    return f.canConnect
      ? step('connect','Per lavorare sulle richieste serve collegare il numero del team. Vuoi aprire la configurazione?','open-number-setup','open')
      : help('Il numero del team non è ancora collegato. Prepariamo una richiesta al responsabile?');
  }
  if (f.numberSelected === 'no') return step('choose-number','Il numero del team è già collegato. Apri WhatsApp e scegli quello su cui lavorare.','open-conversations','open');
  if (f.selfCanAccess === 'unknown') return refresh();
  if (f.selfCanAccess === 'no') return f.canManageAccess
    ? step('self-access','Il numero è collegato, ma il tuo accesso non è disponibile. Vuoi aprire la gestione accessi?','open-access','open')
    : help('Il numero è collegato, ma serve che il responsabile ti dia accesso. Prepariamo la richiesta?');
  if (f.colleagueSelected === 'no') return step('choose-colleague',f.receiving?'Con quale collega stai facendo il primo passaggio?':'Con quale collega vuoi gestire la prima conversazione?','choose-colleague','question');
  if (f.colleagueActive === 'unknown') return refresh();
  if (f.colleagueActive === 'no') {
    if (f.invitationPending === 'unknown') return refresh();
    if (f.invitationPending === 'yes') return step('wait-invite','L’invito risulta in attesa. Possiamo riprendere quando il collega avrà accettato.','refresh-evidence','wait');
    return f.canInvite
      ? step('invite','Chi vuoi coinvolgere? Posso aprire il modulo per scegliere email e ruolo. L’invio avrà una conferma separata.','open-invite','open')
      : help('Per aggiungere un collega serve un invito del responsabile. Prepariamo una richiesta?');
  }
  if (f.colleagueCanAccess === 'unknown') return refresh();
  if (f.colleagueCanAccess === 'no') return f.canManageAccess
    ? step('colleague-access','Il collega è nel team, ma non ha accesso a questo numero. Vuoi vedere le impostazioni di accesso?','open-access','open')
    : help('Il collega è nel team, ma deve ricevere accesso al numero dal responsabile. Prepariamo la richiesta?');
  if (f.conversationSelected === 'unknown') return refresh();
  if (f.conversationSelected === 'no') return step('select-conversation','Scegli una conversazione su cui lavorare insieme. Vuoi aprire WhatsApp?','open-conversations','open');
  if (f.assignedToColleague === 'unknown') return refresh();
  if (f.receiving && f.assignedToColleague === 'no') return step('wait-assignment','Questa conversazione non risulta ancora assegnata a te. Chiedi al collega di affidartela, poi ricontrolliamo.','refresh-evidence','wait');
  if (f.assignedToColleague === 'no') return f.canAssign
    ? step('assign','Vuoi affidare questa conversazione al collega? Ti mostro la modifica prima di salvarla.','propose-assignment','propose')
    : help('Non hai il permesso di assegnare questa conversazione. Prepariamo una richiesta al responsabile?');
  if (f.recipientVerified !== 'yes') return step('recipient-check',f.receiving?'La conversazione è assegnata a te. Aggiungi una nota interna, poi verifichiamo il passaggio.':'L’assegnazione è salvata. Verifichiamo che il collega possa aprire e gestire questa stessa conversazione.','verify-recipient','read');
  return step('complete','Il collega ha accesso alla conversazione assegnata e ne ha verificato la gestione. Potete continuare da qui.',null,'done');
}

/** Persist only interaction preferences; a stored step/completed flag never proves CRM state. */
export interface HabiPreferences { version: 1; persona: Persona | null; hidden: boolean }
export function resumePreferences(raw: unknown): HabiPreferences {
  if (!raw || typeof raw !== 'object') return {version:1,persona:null,hidden:false};
  const value = raw as Record<string,unknown>;
  if (value.version !== 1) return {version:1,persona:null,hidden:false};
  return {version:1,persona:value.persona === 'owner' || value.persona === 'invited' ? value.persona : null,hidden:value.hidden === true};
}

/** Code mappings verified 2026-10-25. IDs are stable application targets, not model CSS selectors. */
export const habiCapabilities = [
  {id:'number.connect', target:'whatsapp.add-number', source:'src/components/settings/whatsapp/WhatsAppNumbersTab.tsx', mode:'guided'},
  {id:'team.invite', target:'team.invite', source:'src/components/settings/InviteRowsForm.tsx', mode:'guided'},
  {id:'number.access', target:'whatsapp.manage-access', source:'src/components/settings/whatsapp/WhatsAppAccessManagerModal.tsx', mode:'guided'},
  {id:'conversation.assign', target:'whatsapp.assign-operator', source:'src/components/whatsapp2/shared/OperatorAssignPicker.tsx', mode:'needs-verified-adapter'},
  {id:'conversation.note', target:'whatsapp.internal-note', source:'src/components/whatsapp2/chat/ChatNotesPanel.tsx', mode:'guided'},
] as const;
